import { StyledEngineProvider } from "@mui/material";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { createRoot } from "react-dom/client";
import {
  RouterProvider,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

import { CaseSummaryProvider } from "./app/components/cm/Dashboard/FullPages/ClientDetail/CaseSummaryV2/context/CaseSummaryContext";
import ToastProvider from "./app/components/modules/Common/ToastProvider";
import { ActionLogProvider } from "./app/context/AccountabilityLogContext";
import { DataProvider } from "./app/context/DataContext";
import EventsProvider from "./app/context/EventsProvider";
import { ProCliProvider } from "./app/context/ProviderSearchContext";
import { SearchProvider } from "./app/context/SearchContext";
import "./index.css";
import "./lib/itn-ui-kit/services/register-services";
import SentryUserTracker from "./app/observability/SentryUserTracker";
// loaders will run as soon as the router is imported, so anything that needs to run before loaders needs to be imported above this
import router from "./app/router";
import reportWebVitals from "./reportWebVitals";
import { registerServiceWorker } from "./serviceWorkerRegister";

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const SENTRY_ENV = process.env.REACT_APP_SENTRY_ENV || "local";

const TRACES_RATE = Number(process.env.REACT_APP_SENTRY_SAMPLE_RATE ?? 0.1);
const REPLAY_SESSION_RATE = Number(process.env.REACT_APP_SENTRY_REPLAY_SAMPLE_RATE ?? 0);
const REPLAY_ERROR_RATE = Number(process.env.REACT_APP_SENTRY_REPLAY_ERROR_RATE ?? 1);

const queryClient = new QueryClient();

const container = document.getElementById("root");
const root = createRoot(container);

registerServiceWorker();

// --- Sentry init ---
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        // See docs for support of different versions/variations of React Router
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
      }),
    ],

    // Use numbers for sampling (envs come as strings)
    tracesSampleRate: TRACES_RATE,

    // Control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "https://dev-app.healthnsync.com",
      "https://qa-app.healthnsync.com",
      "https://staging-app.healthnsync.com",
      "https://app.healthnsync.com",
    ],

    // Replay sampling: % of sessions  % on error
    replaysSessionSampleRate: REPLAY_SESSION_RATE,
    replaysOnErrorSampleRate: REPLAY_ERROR_RATE,

    // Always pass a STRING release (prevents 'Discarded session because of missing or non-string release')
    release: SENTRY_ENV,
  });
} else {
  // No DSN in this env → skip transport to avoid 'Transport disabled' noise
  // You can still keep the codepaths; Sentry APIs will no-op.
  if (SENTRY_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.info("[Sentry] DSN not set. Sentry disabled for this environment.");
  }
}

root.render(
  <QueryClientProvider client={queryClient}>
    <SentryUserTracker />
    <EventsProvider>
      <DataProvider>
        <SearchProvider>
          <ProCliProvider>
            <ActionLogProvider>
              <CaseSummaryProvider>
                <StyledEngineProvider injectfirst>
                  <ToastProvider />
                  <RouterProvider router={router} />
                </StyledEngineProvider>
              </CaseSummaryProvider>
            </ActionLogProvider>
          </ProCliProvider>
        </SearchProvider>
      </DataProvider>
    </EventsProvider>
  </QueryClientProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
